(function () {
  'use strict';
  const singleton = '__igneousRelatedCart';
  if (window[singleton]) {
    window[singleton].scan();
    return;
  }
  const selector = '.igneous-related-gear[data-related-cart-aware]';
  let active = null;
  let revision = 0;
  let request = null;
  let queued = null;
  let scanQueued = false;
  let resizeQueued = false;
  let navigationPath = null;
  let everMounted = false;
  let recoveryRequested = false;

  function productId(value) {
    if (typeof value !== 'string' && typeof value !== 'number') throw Error('Invalid product ID');
    if (typeof value === 'number' && (!Number.isSafeInteger(value) || value <= 0)) throw Error('Invalid product ID');
    const result = String(value);
    if (!/^[1-9]\d{0,15}$/.test(result)) throw Error('Invalid product ID');
    return result;
  }
  function cartIds(cart) {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) throw Error('Invalid cart snapshot');
    let count = 0;
    const ids = new Set();
    for (const item of cart.items) {
      if (!item || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) throw Error('Invalid cart line');
      ids.add(productId(item.product_id));
      count += item.quantity;
    }
    if (count !== cart.item_count) throw Error('Invalid cart count');
    return ids;
  }
  function invalidate() {
    revision++;
    queued = null;
    if (request) request.controller.abort();
    request = null;
    return revision;
  }
  function current(state, token) {
    return state === active && token === revision && state.section.isConnected && (!state.productPath || location.pathname === state.productPath) && !navigationPath;
  }
  function sourceUrls(section) {
    const root = new URL(section.dataset.relatedRootUrl, location.origin);
    const product = new URL(section.dataset.relatedSourceUrl, location.origin);
    const handle = section.dataset.igneousRelatedSourceHandle;
    if (root.origin !== location.origin || product.origin !== location.origin || !root.pathname.endsWith('/') || product.pathname !== root.pathname + 'products/' + handle) throw Error('Invalid related routes');
    const cart = new URL('cart.js', root);
    product.search = '';
    product.hash = '';
    product.searchParams.set('section_id', 'igneous-related-gear-refresh');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) product.searchParams.set('preview_theme_id', preview);
    return { cart: cart.href, section: product.href, productPath: product.pathname };
  }
  function payload(container, state) {
    const elements = container.querySelectorAll('[data-related-payload]');
    if (elements.length !== 1) throw Error('Invalid related payload');
    const node = elements[0];
    const scripts = node.querySelectorAll('script[data-related-cart-meta][type="application/json"]');
    if (scripts.length !== 1) throw Error('Invalid related metadata');
    const meta = JSON.parse(scripts[0].textContent);
    if (meta.version !== 1 || productId(meta.sourceId) !== state.sourceId || meta.sourceHandle !== state.sourceHandle || !Array.isArray(meta.cartRules)) throw Error('Wrong related source');
    const rules = meta.cartRules.map(rule => {
      if (!Array.isArray(rule.blockedHandles) || rule.blockedHandles.some(handle => typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(handle))) throw Error('Invalid cart exclusion');
      return { productId: productId(rule.productId), blockedHandles: rule.blockedHandles.slice() };
    });
    const pool = [];
    const seen = new Set();
    let rank = 0;
    for (const template of node.querySelectorAll('template[data-related-candidate]')) {
      const id = productId(template.dataset.productId);
      const handle = template.dataset.productHandle;
      const nextRank = Number(template.dataset.rank);
      const card = template.content.firstElementChild;
      const link = card && card.querySelector('a.igneous-related-gear__link');
      if (!Number.isSafeInteger(nextRank) || nextRank <= rank || !/^[a-z0-9][a-z0-9-]*$/.test(handle) || id === state.sourceId || seen.has(id) || !card || card.tagName !== 'LI' || template.content.children.length !== 1 || !link || link.dataset.igneousRelatedProductId !== id || link.dataset.igneousRelatedProductHandle !== handle || link.dataset.igneousRelatedAvailable !== 'true' || template.content.querySelector('script,style,link,iframe,object,embed,form,button') || card.querySelectorAll('a').length !== 1) throw Error('Invalid related candidate');
      const url = new URL(link.getAttribute('href'), location.origin);
      const root = new URL(state.section.dataset.relatedRootUrl, location.origin);
      if (url.origin !== location.origin || ![root.pathname + 'products/' + handle, '/products/' + handle].includes(url.pathname) || handle === 'tent-tool') throw Error('Invalid candidate destination');
      seen.add(id);
      rank = nextRank;
      pool.push({ id: id, handle: handle, rank: rank, template: template });
    }
    if (pool.length > 16) throw Error('Related pool exceeds current catalog');
    return { pool: pool, rules: rules, initialCart: meta.initialCart, node: node };
  }
  function reserve(state) {
    const width = state.list.getBoundingClientRect().width;
    if (width !== state.reservedWidth) {
      state.list.style.minHeight = '';
      state.reservedHeight = 0;
      state.reservedWidth = width;
    }
    const height = state.list.getBoundingClientRect().height;
    if (height > 0) state.reservedHeight = Math.max(state.reservedHeight, height);
    if (state.reservedHeight > 0) state.list.style.minHeight = state.reservedHeight + 'px';
  }
  function headingFocus() {
    if (window.theme && window.theme.state && window.theme.state.cartOpen) return;
    const heading = document.querySelector('#MainContent h1.product__title');
    if (!heading || !heading.isConnected) return;
    const temporary = !heading.hasAttribute('tabindex');
    if (temporary) {
      heading.setAttribute('tabindex', '-1');
      heading.addEventListener('blur', function () { heading.removeAttribute('tabindex'); }, { once: true });
    }
    heading.focus({ preventScroll: true });
  }
  function visibleLink(link) {
    const rect = link.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }
  function uncertain(state) {
    if (!state || !state.section.isConnected) return;
    const focused = state.list && state.list.contains(document.activeElement);
    if (state.list) reserve(state);
    state.section.dataset.relatedState = 'uncertain';
    state.section.setAttribute('aria-hidden', 'true');
    state.section.inert = true;
    if (focused) headingFocus();
  }
  function render(state, ids, status) {
    const blocked = new Set();
    for (const rule of state.rules) if (ids.has(rule.productId)) for (const handle of rule.blockedHandles) blocked.add(handle);
    const selected = state.pool.filter(candidate => !ids.has(candidate.id) && !blocked.has(candidate.handle)).slice(0, 4);
    const keys = selected.map(candidate => candidate.id).join(',');
    const cards = selected.map(candidate => {
      const card = candidate.template.content.firstElementChild.cloneNode(true);
      for (const image of card.querySelectorAll('img')) image.sizes = state.imageSizes;
      return card;
    });
    const contentKey = cards.map(card => card.outerHTML).join('');
    let restoreFocus = null;
    reserve(state);
    if (keys !== state.keys || contentKey !== state.contentKey) {
      const focused = document.activeElement;
      const ownedFocus = state.list.contains(focused);
      const focusedLink = ownedFocus && focused.closest('a[data-igneous-related-product-id]');
      const focusedId = focusedLink && focusedLink.dataset.igneousRelatedProductId;
      const links = Array.from(state.list.querySelectorAll('a.igneous-related-gear__link'));
      const focusIndex = focusedLink ? links.indexOf(focusedLink) : 0;
      const fragment = document.createDocumentFragment();
      for (const card of cards) fragment.appendChild(card);
      state.list.replaceChildren(fragment);
      state.keys = keys;
      state.contentKey = contentKey;
      if (ownedFocus) restoreFocus = { element: focused, id: focusedId, index: focusIndex };
    }
    if (selected.length > 0) state.section.hidden = false;
    state.list.dataset.relatedCount = String(selected.length);
    state.section.dataset.relatedState = selected.length ? status : 'empty';
    if (selected.length) state.section.removeAttribute('aria-hidden');
    else state.section.setAttribute('aria-hidden', 'true');
    state.section.inert = selected.length === 0;
    reserve(state);
    if (restoreFocus && (document.activeElement === restoreFocus.element || document.activeElement === document.body) && !(window.theme && window.theme.state && window.theme.state.cartOpen)) {
      const targets = Array.from(state.list.querySelectorAll('a.igneous-related-gear__link')).filter(visibleLink);
      const target = targets.find(link => link.dataset.igneousRelatedProductId === restoreFocus.id) || targets[Math.min(Math.max(0, restoreFocus.index), targets.length - 1)];
      if (target) target.focus({ preventScroll: true });
      else headingFocus();
    }
  }
  async function fetchChecked(url, job, mode) {
    const response = await window.fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: job.controller.signal, headers: { Accept: mode === 'json' ? 'application/json' : 'text/html' } });
    if (!response.ok) throw Error('Related read failed');
    return mode === 'json' ? response.json() : response.text();
  }
  function schedule(state, full, hide) {
    const token = invalidate();
    if (hide) uncertain(state);
    state.section.setAttribute('aria-busy', 'true');
    queued = { state: state, full: full, token: token };
    queueMicrotask(async function () {
      const plan = queued;
      if (!plan || plan.token !== token || !current(state, token)) return;
      queued = null;
      const job = { controller: new AbortController() };
      request = job;
      const timer = setTimeout(function () { job.controller.abort(); }, 10000);
      try {
        const urls = sourceUrls(state.section);
        if (location.pathname !== urls.productPath) throw Error('Related navigation pending');
        const results = await Promise.all([full ? fetchChecked(urls.cart, job, 'json') : Promise.resolve(null), fetchChecked(urls.section, job, 'text')]);
        if (!current(state, token)) return;
        const parsed = new DOMParser().parseFromString(results[1], 'text/html');
        const fresh = payload(parsed, state);
        const ids = full ? cartIds(results[0]) : state.cartIds;
        if (!ids) throw Error('Cart membership unknown');
        if (!current(state, token)) return;
        state.pool = fresh.pool;
        state.rules = fresh.rules;
        state.cartIds = ids;
        render(state, ids, 'ready');
      } catch (error) {
        if (current(state, token)) uncertain(state);
      } finally {
        job.controller.abort();
        clearTimeout(timer);
        if (current(state, token)) state.section.removeAttribute('aria-busy');
        if (request === job) request = null;
      }
    });
  }
  function scan() {
    scanQueued = false;
    const recovering = recoveryRequested || everMounted;
    recoveryRequested = false;
    const section = document.querySelector('#MainContent ' + selector);
    if (!section) {
      invalidate();
      active = null;
      return;
    }
    let urls;
    try {
      urls = sourceUrls(section);
      if (location.pathname !== urls.productPath || navigationPath && navigationPath !== urls.productPath) { if (active) uncertain(active); return; }
      navigationPath = null;
    } catch (error) { if (active) uncertain(active); return; }
    if (active && active.section === section && active.sourceId === section.dataset.igneousRelatedSourceId && active.sourceHandle === section.dataset.igneousRelatedSourceHandle) {
      try {
        active.list = section.querySelector('.igneous-related-gear__list');
        active.imageSizes = section.dataset.relatedImageSizes;
        const fresh = payload(section, active);
        active.pool = fresh.pool;
        active.rules = fresh.rules;
        active.keys = Array.from(active.list.querySelectorAll('a.igneous-related-gear__link')).map(link => link.dataset.igneousRelatedProductId).join(',');
        active.contentKey = Array.from(active.list.children).map(card => card.outerHTML).join('');
        schedule(active, true, true);
      } catch (error) { uncertain(active); }
      return;
    }
    invalidate();
    if (active) uncertain(active);
    active = null;
    const state = { section: section, list: section.querySelector('.igneous-related-gear__list'), sourceId: section.dataset.igneousRelatedSourceId, sourceHandle: section.dataset.igneousRelatedSourceHandle, imageSizes: section.dataset.relatedImageSizes, productPath: urls.productPath, reservedHeight: 0, reservedWidth: 0, keys: null, contentKey: null, cartIds: null };
    active = state;
    try {
      if (!state.list || !state.imageSizes) throw Error('Invalid native related row');
      productId(state.sourceId);
      sourceUrls(section);
      const initial = payload(section, state);
      state.pool = initial.pool;
      state.rules = initial.rules;
      state.keys = Array.from(state.list.querySelectorAll('a.igneous-related-gear__link')).map(link => link.dataset.igneousRelatedProductId).join(',');
      state.contentKey = Array.from(state.list.children).map(card => card.outerHTML).join('');
      everMounted = true;
      if (!recovering) {
        state.cartIds = cartIds(initial.initialCart);
        render(state, state.cartIds, 'ready');
      }
      schedule(state, true, recovering);
    } catch (error) {
      if (state.list) uncertain(state);
    }
  }
  function queueScan(recover) {
    if (recover) {
      recoveryRequested = true;
      if (active) uncertain(active);
      const section = document.querySelector('#MainContent ' + selector);
      if (section && (!active || section !== active.section)) {
        section.dataset.relatedState = 'uncertain';
        section.setAttribute('aria-hidden', 'true');
        section.inert = true;
      }
    }
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(scan);
  }
  document.addEventListener('theme:cart:change', function (event) {
    if (!active) return;
    if (navigationPath || location.pathname !== active.productPath) { invalidate(); uncertain(active); return; }
    try {
      const ids = cartIds(event.detail && event.detail.cart);
      invalidate();
      active.cartIds = ids;
      render(active, ids, 'refreshing');
      schedule(active, false, false);
    } catch (error) {
      schedule(active, true, true);
    }
  });
  for (const event of ['theme:cart:reload', 'theme:cart:init']) document.addEventListener(event, function () { if (active) schedule(active, true, true); });
  window.addEventListener('theme:section:navigate', function (event) {
    if (!active) return;
    if (event.detail && event.detail.sectionId && event.detail.sectionId !== active.section.dataset.relatedSectionId) return;
    try { navigationPath = new URL(event.detail && event.detail.url || location.href, location.origin).pathname; }
    catch (error) { navigationPath = location.pathname; }
    invalidate();
    uncertain(active);
  });
  for (const event of ['theme:section:load', 'theme:section:update']) window.addEventListener(event, function (event) {
    if (active && event.detail && event.detail.sectionId && event.detail.sectionId !== active.section.dataset.relatedSectionId) return;
    queueScan(true);
  });
  document.addEventListener('shopify:section:load', function () { queueScan(true); });
  document.addEventListener('shopify:section:unload', function (event) { if (active && event.target.contains(active.section)) { invalidate(); uncertain(active); active = null; } });
  window.addEventListener('pageshow', function (event) { if (event.persisted === false && active) return; navigationPath = null; queueScan(true); });
  window.addEventListener('resize', function () {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(function () {
      resizeQueued = false;
      if (!active) return;
      reserve(active);
      const focused = document.activeElement;
      if (active.list.contains(focused) && !visibleLink(focused) && !(window.theme && window.theme.state && window.theme.state.cartOpen)) {
        const target = Array.from(active.list.querySelectorAll('a.igneous-related-gear__link')).find(visibleLink);
        if (target && !active.section.inert) target.focus({ preventScroll: true });
        else headingFocus();
      }
    });
  });
  window[singleton] = { scan: function () { queueScan(true); } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { queueScan(false); }, { once: true });
  else queueScan(false);
})();
